// Cache-invalidation targets parsed from the agent's cio CLI mutations — drives the
// hydra-nav-state refresh after an approved bash mutation.

const DYNAMIC_PLACEHOLDER = '__DYNAMIC__';

export type CioResource = {
  resourceType: string;
  resourceId: string | undefined;
  parentType: string | undefined;
  parentId: string | undefined;
};

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Quote-aware so separators or JSON payloads inside quotes don't split the command.
function tokenizeSegments(command: string): string[][] {
  const segments: string[][] = [];
  let tokens: string[] = [];
  let current = '';
  let hasContent = false;
  let quote: "'" | '"' | null = null;

  const endToken = () => {
    if (hasContent) {
      tokens.push(current);
      current = '';
      hasContent = false;
    }
  };
  const endSegment = () => {
    endToken();
    if (tokens.length > 0) {
      segments.push(tokens);
      tokens = [];
    }
  };

  for (let i = 0; i < command.length; i++) {
    const ch = command[i];
    if (quote != null) {
      if (ch === quote) {
        quote = null;
      } else if (quote === '"' && ch === '\\' && i + 1 < command.length) {
        current += command[++i];
      } else {
        current += ch;
      }
      continue;
    }
    // Shell line continuation: join across the escaped newline.
    if (ch === '\\' && command[i + 1] === '\n') {
      i++;
      continue;
    }
    if (ch === "'" || ch === '"') {
      quote = ch;
      hasContent = true;
      continue;
    }
    if (ch === ' ' || ch === '\t') {
      endToken();
      continue;
    }
    // Separators; the empty middle segment from `&&`/`||` is dropped by endSegment.
    if (ch === ';' || ch === '\n' || ch === '|' || ch === '&') {
      endSegment();
      continue;
    }
    current += ch;
    hasContent = true;
  }
  endSegment();
  return segments;
}

function resolvePath(path: string, paramsJson: string | undefined, vars: Record<string, string>): string {
  let params: Record<string, unknown> = {};
  if (paramsJson != null) {
    try {
      const parsed: unknown = JSON.parse(paramsJson);
      if (parsed != null && typeof parsed === 'object') {
        params = parsed as Record<string, unknown>;
      }
    } catch {
      // Unparseable --params: leave {key} placeholders to degrade to DYNAMIC below.
    }
  }
  // $VAR before {key}: the {key} regex would otherwise eat the braces in `${VAR}`.
  return path
    .replace(/\$\{([A-Za-z_][A-Za-z0-9_]*)\}|\$([A-Za-z_][A-Za-z0-9_]*)/g, (_match, braced, bare) => {
      return vars[braced ?? bare] ?? DYNAMIC_PLACEHOLDER;
    })
    .replace(/\{([^{}]+)\}/g, (_match, key: string) => {
      const value = params[key];
      return value != null ? String(value) : DYNAMIC_PLACEHOLDER;
    });
}

function isIdentifier(segment: string): boolean {
  if (/^\d+$/.test(segment)) return true;
  if (/^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i.test(segment)) return true;
  if (/^[\da-f]{8,}$/i.test(segment)) return true;
  // Treat dynamic placeholders as ids so they're stripped from the resource type.
  if (segment.includes(DYNAMIC_PLACEHOLDER)) return true;
  return false;
}

type ResourcePair = { type: string; id?: string };

// Path → {type, id} pairs; ids attach to the preceding type. environments/{id} is a
// namespace prefix, dropped unless it's the terminal pair.
function pathPairs(path: string): ResourcePair[] {
  const segments = path
    .split('/')
    .filter(Boolean)
    .filter((segment) => !/^(v\d+|api)$/i.test(segment));
  const pairs: ResourcePair[] = [];
  for (const segment of segments) {
    if (isIdentifier(segment)) {
      const last = pairs[pairs.length - 1];
      if (last != null && last.id == null) {
        last.id = segment;
      }
      continue;
    }
    pairs.push({ type: segment });
  }
  return pairs.filter((pair, index) => index === pairs.length - 1 || pair.type.toLowerCase() !== 'environments');
}

function parseCioSegment(tokens: string[]): CioResource | null {
  // Capture VAR=value prefixes so $VAR in the path resolves (e.g. `ID=123 cio api .../$ID`).
  const vars: Record<string, string> = {};
  let index = 0;
  while (index < tokens.length && /^[A-Za-z_][A-Za-z0-9_]*=/.test(tokens[index])) {
    const eq = tokens[index].indexOf('=');
    vars[tokens[index].slice(0, eq)] = tokens[index].slice(eq + 1);
    index++;
  }
  if (tokens[index] !== 'cio') {
    return null;
  }
  index++;

  let sawApi = false;
  let path: string | undefined;
  let method: string | undefined;
  let hasBody = false;
  let paramsJson: string | undefined;

  for (; index < tokens.length; index++) {
    let token = tokens[index];
    let eqValue: string | undefined;
    if (token.startsWith('--') && token.includes('=')) {
      const eq = token.indexOf('=');
      eqValue = token.slice(eq + 1);
      token = token.slice(0, eq);
    }

    if (token === '-X' || token === '--method') {
      method = eqValue ?? tokens[++index];
      continue;
    }
    if (token.startsWith('-X') && token.length > 2) {
      method = token.slice(2);
      continue;
    }
    if (token === '--json') {
      hasBody = true;
      if (eqValue == null) index++;
      continue;
    }
    if (token === '--params') {
      paramsJson = eqValue ?? tokens[++index];
      continue;
    }
    if (token.startsWith('-')) {
      continue;
    }
    if (!sawApi) {
      // skills/schema/prime/auth don't touch workspace resources.
      if (token !== 'api') {
        return null;
      }
      sawApi = true;
      continue;
    }
    // The path is the only `/`-prefixed positional (cio prepends `/`), so flag values can't be it.
    if (token.startsWith('/')) {
      path ??= token;
    }
  }

  if (!sawApi || path == null) {
    return null;
  }

  const resolvedMethod = (method ?? (hasBody ? 'POST' : 'GET')).toUpperCase();
  if (!MUTATING_METHODS.has(resolvedMethod)) {
    return null;
  }

  const pairs = pathPairs(resolvePath(path, paramsJson, vars).split(/[?#]/)[0]);
  const target = pairs[pairs.length - 1];
  // A dynamic terminal type or an all-identifier path can't be targeted.
  if (target == null || target.type.includes(DYNAMIC_PLACEHOLDER)) {
    return null;
  }
  const concreteId = (id: string | undefined) => (id != null && !id.includes(DYNAMIC_PLACEHOLDER) ? id : undefined);
  const parent = pairs
    .slice(0, -1)
    .reverse()
    .find((pair) => concreteId(pair.id) != null);
  return {
    resourceType: target.type,
    resourceId: concreteId(target.id),
    parentType: parent?.type,
    parentId: parent?.id,
  };
}

// Flat `cio api` segments only (optionally VAR=value-prefixed); no loops, wrappers,
// subshells, or curl — best-effort, matching what the agent emits.
export function parseCioMutations(command: string): CioResource[] {
  return tokenizeSegments(command)
    .map(parseCioSegment)
    .filter((resource): resource is CioResource => resource != null);
}
